import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Ban, CalendarClock, Edit, Plus, RotateCw, Save, ShieldCheck, X } from "lucide-react";
import AdminCardActions from "../components/AdminCardActions";
import CollapsibleMobileCard from "../components/CollapsibleMobileCard";
import FilterPopover from "../components/FilterPopover";
import ToolbarSearch from "../components/ToolbarSearch";
import {
    createAdminSensor,
    fetchAdminSensors,
    manageAdminSensorLicense,
    updateAdminSensor,
    verifyAdminSensorLicense,
} from "../services/sensors";
import { fetchConfigValues } from "../services/configs";
import "../styles/sensors.css";

const SENSOR_TYPE_CONFIG = {
    table_name: "sensor_devices",
    column_name: "sensor_type",
};

const SENSOR_FAMILY_CONFIG = {
    table_name: "sensor_devices",
    column_name: "sensor_family",
};

const DEFAULT_SENSOR_FAMILY_OPTIONS = [
    "Середовище",
    "Безпека",
    "Інженерні системи",
    "Загальна категорія",
];

const emptySensor = {
    device_uid: "",
    license_token: "",
    display_name: "",
    sensor_family: "",
    sensor_type: "",
    hardware_model: "",
    firmware_variant: "",
    descriptions: "",
    install_notes: "",
    pairing_code: "",
    is_private: true,
    is_active: true,
};

const SENSOR_TABLE_LEAD_FIELDS = [
    "device_uid",
    "display_name",
    "sensor_type",
    "sensor_family",
    "hardware_model",
    "firmware_variant",
];

const SENSOR_TABLE_TRAIL_FIELDS = [
    "pairing_code",
    "descriptions",
    "install_notes",
];

const getAttachmentLabel = (sensor) => {
    if (sensor.is_private) {
        if (sensor.client_name) return `Клієнт: ${sensor.client_name}`;
        return "Ще не прив'язаний";
    }
    if (sensor.location_name && sensor.claimed_by_operator_name) {
        return `Оператор: ${sensor.claimed_by_operator_name} • ${sensor.location_name}`;
    }
    if (sensor.location_name) return `Локація: ${sensor.location_name}`;
    return "Ще не доданий оператором";
};

const getAttachmentState = (sensor) => {
    if (sensor.is_private) {
        return sensor.client_id ? "attached" : "free";
    }
    return sensor.location_id || sensor.claimed_by_operator_id ? "attached" : "free";
};

const normalizeOptionValues = (values) => {
    const seen = new Set();
    return (values || []).reduce((acc, item) => {
        const normalized = String(item || "").trim();
        const key = normalized.toLowerCase();
        if (!normalized || seen.has(key)) return acc;
        seen.add(key);
        acc.push(normalized);
        return acc;
    }, []);
};

const getSensorFamilyLabel = (value) => {
    const normalized = String(value || "").trim().toLowerCase();
    return {
        environment: "Середовище",
        safety: "Безпека",
        utility: "Інженерні системи",
        generic: "Загальна категорія",
    }[normalized] || value || "Не задано";
};

const formatDateTime = (value) => {
    if (!value) return "-";

    const raw = String(value).trim();
    const hasTz = /([zZ]|[+-]\d{2}:?\d{2})$/.test(raw);
    const normalized = hasTz || /^\d+$/.test(raw)
        ? raw
        : `${raw.includes("T") ? raw : raw.replace(" ", "T")}Z`;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat("uk-UA", {
        dateStyle: "short",
        timeStyle: "short",
        timeZone: "Europe/Kyiv",
    }).format(date);
};

const getLicenseBadgeLabel = (sensor) => {
    if (sensor.sensor_license_blocked) return "Заблокована";
    return sensor.sensor_license_active ? "Активна" : "Неактивна";
};

const getLicenseBadgeClass = (sensor) => {
    if (sensor.sensor_license_blocked) return "is-inactive";
    return sensor.sensor_license_active ? "is-active" : "is-inactive";
};

const formatLocalYmd = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const tomorrowLocalYmd = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 1);
    return formatLocalYmd(date);
};

const defaultExtendDateYmd = (sensor) => {
    const current = sensor?.sensor_license_expires_at_utc
        ? new Date(String(sensor.sensor_license_expires_at_utc).trim())
        : null;
    const base = current && !Number.isNaN(current.getTime()) ? current : new Date();
    base.setHours(0, 0, 0, 0);
    base.setFullYear(base.getFullYear() + 1);
    const min = tomorrowLocalYmd();
    const candidate = formatLocalYmd(base);
    return candidate > min ? candidate : min;
};

const normalizePayload = (form, { forUpdate = false } = {}) => {
    const payload = {
        device_uid: String(form.device_uid || "").trim().toUpperCase(),
        display_name: String(form.display_name || "").trim(),
        sensor_family: String(form.sensor_family || "").trim() || "Середовище",
        sensor_type: String(form.sensor_type || "").trim() || "Сенсор",
        hardware_model: String(form.hardware_model || "").trim() || null,
        firmware_variant: String(form.firmware_variant || "").trim() || null,
        descriptions: String(form.descriptions || "").trim() || null,
        install_notes: String(form.install_notes || "").trim() || null,
        auth_token: null,
        pairing_code: String(form.pairing_code || "").trim().toUpperCase() || null,
        notify_client: true,
        notify_operator: form.is_private === true ? false : true,
        is_private: form.is_private === true,
        is_active: form.is_active === true,
    };

    const licenseToken = String(form.license_token || "").replace(/\s+/g, "");
    if (!forUpdate || licenseToken) {
        payload.license_token = licenseToken || null;
    }

    return payload;
};

const SensorsPageAdmins = ({ isSuperAdmin: _isSuperAdmin = false }) => {
    const [sensors, setSensors] = useState([]);
    const [filteredSensors, setFilteredSensors] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [visibilityFilter, setVisibilityFilter] = useState("all");
    const [attachmentFilter, setAttachmentFilter] = useState("all");
    const [licenseFilter, setLicenseFilter] = useState("all");
    const [sortBy, setSortBy] = useState("display_name");
    const [sortDir, setSortDir] = useState("asc");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [editedData, setEditedData] = useState(emptySensor);
    const [originalData, setOriginalData] = useState(emptySensor);
    const [newSensor, setNewSensor] = useState(emptySensor);
    const [showAddModal, setShowAddModal] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [sensorTypeOptions, setSensorTypeOptions] = useState([]);
    const [sensorFamilyOptions, setSensorFamilyOptions] = useState(DEFAULT_SENSOR_FAMILY_OPTIONS);
    const [licenseVerifyResult, setLicenseVerifyResult] = useState(null);
    const [verifyingLicense, setVerifyingLicense] = useState(false);
    const [createdSensorInfo, setCreatedSensorInfo] = useState(null);
    const [extendTarget, setExtendTarget] = useState(null);
    const [extendDate, setExtendDate] = useState("");

    const loadSensorTypes = async (sensorItems = []) => {
        const configValues = await fetchConfigValues(SENSOR_TYPE_CONFIG).catch(() => []);
        const configOptions = normalizeOptionValues((configValues || []).map((item) => item.value_name));
        const existingOptions = normalizeOptionValues((sensorItems || []).map((item) => item.sensor_type));
        const mergedOptions = normalizeOptionValues([...configOptions, ...existingOptions]);

        setSensorTypeOptions(mergedOptions);
        setNewSensor((prev) => ({
            ...prev,
            sensor_type: mergedOptions.includes(prev.sensor_type) ? prev.sensor_type : (mergedOptions[0] || ""),
        }));
    };

    const loadSensorFamilies = async (sensorItems = []) => {
        const configValues = await fetchConfigValues(SENSOR_FAMILY_CONFIG).catch(() => []);
        const configOptions = normalizeOptionValues((configValues || []).map((item) => item.value_name));
        const existingOptions = normalizeOptionValues((sensorItems || []).map((item) => getSensorFamilyLabel(item.sensor_family)));
        const mergedOptions = normalizeOptionValues([...configOptions, ...DEFAULT_SENSOR_FAMILY_OPTIONS, ...existingOptions]);

        setSensorFamilyOptions(mergedOptions);
        setNewSensor((prev) => ({
            ...prev,
            sensor_family: mergedOptions.includes(prev.sensor_family) ? prev.sensor_family : (mergedOptions[0] || ""),
        }));
    };

    const loadSensors = useCallback(async () => {
        try {
            setLoading(true);
            setMessage("");
            const data = await fetchAdminSensors();
            const normalizedData = Array.isArray(data)
                ? data.map((item) => ({
                    ...item,
                    sensor_family: getSensorFamilyLabel(item.sensor_family),
                }))
                : [];

            setSensors(normalizedData);
            await Promise.all([
                loadSensorTypes(normalizedData),
                loadSensorFamilies(normalizedData),
            ]);
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSensors();

        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [loadSensors]);

    useEffect(() => {
        const q = searchQuery.toLowerCase();
        const filtered = sensors.filter((sensor) => {
            const matchesSearch =
                (sensor.device_uid || "").toString().toLowerCase().includes(q) ||
                (sensor.display_name || "").toString().toLowerCase().includes(q) ||
                (sensor.sensor_type || "").toString().toLowerCase().includes(q) ||
                (sensor.sensor_family || "").toString().toLowerCase().includes(q) ||
                (sensor.hardware_model || "").toString().toLowerCase().includes(q) ||
                (sensor.firmware_variant || "").toString().toLowerCase().includes(q) ||
                (sensor.descriptions || "").toString().toLowerCase().includes(q);

            const matchesVisibility =
                visibilityFilter === "all" ||
                (visibilityFilter === "private" && sensor.is_private) ||
                (visibilityFilter === "public" && !sensor.is_private);

            const sensorAttachmentState = getAttachmentState(sensor);
            const matchesAttachment =
                attachmentFilter === "all" ||
                attachmentFilter === sensorAttachmentState;

            const matchesLicense =
                licenseFilter === "all" ||
                (licenseFilter === "active" && sensor.sensor_license_active) ||
                (licenseFilter === "inactive" && !sensor.sensor_license_active);

            return matchesSearch && matchesVisibility && matchesAttachment && matchesLicense;
        });
        setFilteredSensors(filtered);
    }, [sensors, searchQuery, visibilityFilter, attachmentFilter, licenseFilter]);

    const sortedSensors = useMemo(() => {
        const sorted = [...filteredSensors];
        sorted.sort((a, b) => {
            let valA = a[sortBy];
            let valB = b[sortBy];

            if (typeof valA === "boolean") {
                valA = valA ? 1 : 0;
                valB = valB ? 1 : 0;
            } else {
                valA = String(valA || "").toLowerCase();
                valB = String(valB || "").toLowerCase();
            }

            if (sortDir === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            }
            return valA < valB ? 1 : valA > valB ? -1 : 0;
        });
        return sorted;
    }, [filteredSensors, sortBy, sortDir]);

    const handleFieldChange = (setter) => (event) => {
        const { name, value, type, checked } = event.target;
        setter((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleEdit = (sensor) => {
        const normalizedSensor = {
            ...sensor,
            sensor_family: getSensorFamilyLabel(sensor.sensor_family),
        };
        setEditingId(sensor.id);
        setExpandedId(sensor.id);
        setEditedData(normalizedSensor);
        setOriginalData(normalizedSensor);
    };

    const handleCancelEdit = () => {
        setEditedData(originalData);
        setEditingId(null);
    };

    const openExtendModal = (sensor) => {
        setExtendTarget(sensor);
        setExtendDate(defaultExtendDateYmd(sensor));
        setMessage("");
    };

    const closeExtendModal = () => {
        if (saving) return;
        setExtendTarget(null);
        setExtendDate("");
    };

    const handleConfirmExtendLicense = async () => {
        if (!extendTarget) return;

        const trimmed = String(extendDate || "").trim();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            setMessage("❌ Оберіть дату в календарі");
            return;
        }
        if (trimmed < tomorrowLocalYmd()) {
            setMessage("❌ Дата продовження має бути в майбутньому");
            return;
        }

        try {
            setSaving(true);
            setMessage("");
            await manageAdminSensorLicense(extendTarget.id, {
                action: "extend",
                expires_override_at_utc: `${trimmed}T23:59:59Z`,
            });
            setExtendTarget(null);
            setExtendDate("");
            await loadSensors();
            setMessage(`✅ Ліцензію ${extendTarget.device_uid} продовжено`);
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleExtendLicense = (sensor) => {
        openExtendModal(sensor);
    };

    const handleBlockLicense = async (sensor) => {
        if (!window.confirm(`Заблокувати ліцензію для ${sensor.device_uid}? Сенсор більше не пройде online-check / activate.`)) {
            return;
        }

        try {
            setSaving(true);
            setMessage("");
            await manageAdminSensorLicense(sensor.id, { action: "block" });
            await loadSensors();
            setMessage(`✅ Ліцензію ${sensor.device_uid} заблоковано`);
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleUnblockLicense = async (sensor) => {
        if (!window.confirm(`Розблокувати ліцензію для ${sensor.device_uid}?`)) {
            return;
        }

        try {
            setSaving(true);
            setMessage("");
            await manageAdminSensorLicense(sensor.id, { action: "unblock" });
            await loadSensors();
            setMessage(`✅ Ліцензію ${sensor.device_uid} розблоковано`);
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleVerifyLicense = async (form = newSensor) => {
        if (!String(form.device_uid || "").trim() || !String(form.license_token || "").trim()) {
            setMessage("❌ Для перевірки потрібні серійний номер і sensor key");
            return;
        }

        try {
            setVerifyingLicense(true);
            setMessage("");
            const result = await verifyAdminSensorLicense({
                device_uid: String(form.device_uid || "").trim().toUpperCase(),
                license_token: String(form.license_token || "").replace(/\s+/g, ""),
            });
            setLicenseVerifyResult(result);
            if (result.valid) {
                setMessage("✅ Sensor key підтверджено");
            } else {
                setMessage(`❌ ${result.detail || "Sensor key не пройшов перевірку"}`);
            }
        } catch (error) {
            setLicenseVerifyResult(null);
            setMessage(`❌ ${error.message}`);
        } finally {
            setVerifyingLicense(false);
        }
    };

    const handleCreate = async () => {
        if (!String(newSensor.device_uid || "").trim()) {
            setMessage("❌ Серійний номер сенсора не може бути порожнім");
            return;
        }
        if (!String(newSensor.license_token || "").trim()) {
            setMessage("❌ Для реєстрації сенсора потрібен sensor key");
            return;
        }
        if (!String(newSensor.sensor_type || "").trim()) {
            setMessage("❌ Спочатку додайте типи сенсорів у Системні значення");
            return;
        }

        try {
            setSaving(true);
            setMessage("");
            const createdItem = await createAdminSensor(normalizePayload(newSensor));
            setCreatedSensorInfo(createdItem || null);
            setNewSensor(emptySensor);
            setLicenseVerifyResult(null);
            setShowAddModal(false);
            await loadSensors();
            setMessage("✅ Сенсор активовано в мережі");
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async (sensorId) => {
        if (!String(editedData.sensor_type || "").trim()) {
            setMessage("❌ Для сенсора має бути вибраний тип");
            return;
        }

        try {
            setSaving(true);
            setMessage("");
            await updateAdminSensor(sensorId, normalizePayload(editedData, { forUpdate: true }));
            setEditingId(null);
            setEditedData(emptySensor);
            await loadSensors();
            setMessage("✅ Сенсор оновлено");
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const renderFlagEditor = (form, setter, disabled) => (
        <div className="admin-sensors-flags">
            <label className="admin-sensors-check-inline admin-sensors-mode-select-wrap">
                <span>Режим</span>
                <select
                    value={form.is_private === true ? "private" : "public"}
                    onChange={(event) => setter((prev) => ({
                        ...prev,
                        is_private: event.target.value === "private",
                    }))}
                    disabled={disabled}
                >
                    <option value="public">Публічний</option>
                    <option value="private">Приватний</option>
                </select>
            </label>
            <label className="admin-sensors-check-inline">
                <input
                    type="checkbox"
                    name="is_active"
                    checked={form.is_active === true}
                    onChange={handleFieldChange(setter)}
                    disabled={disabled}
                />
                <span>Активний</span>
            </label>
            <div className="admin-sensors-mode-hint">
                `Публічний` означає спрацювання для оператора і клієнтів локації. `Приватний` означає спрацювання лише для клієнта.
            </div>
        </div>
    );

    const renderFlagsPreview = (sensor) => (
        <div className="admin-sensors-flags admin-sensors-flags-preview">
            <span className="admin-sensors-flag-badge">{sensor.is_private ? "Приватний" : "Публічний"}</span>
            {sensor.is_active ? <span className="admin-sensors-flag-badge is-active">Активний</span> : <span className="admin-sensors-flag-badge is-muted">Вимкнений</span>}
        </div>
    );

    const renderModal = () => {
        if (!showAddModal) return null;

        return (
            <div className="modal-backdrop" onClick={() => setShowAddModal(false)}>
                <div className="modal-content admin-sensors-modal" onClick={(event) => event.stopPropagation()}>
                    <h3>Зареєструвати сенсор</h3>
                    <div className="admin-sensors-modal-grid">
                        <input
                            name="device_uid"
                            placeholder="Серійний номер"
                            value={newSensor.device_uid}
                            onChange={handleFieldChange(setNewSensor)}
                        />
                        <input
                            name="license_token"
                            placeholder="Sensor key"
                            value={newSensor.license_token}
                            onChange={handleFieldChange(setNewSensor)}
                        />
                        <div className="admin-sensors-modal-hint admin-sensors-modal-span-2">
                            1. Прошивка через кабель: `sensor key` записується в `secrets.h` або через UART/BLE на виробництві.
                            2. Тут: перевірте `sensor key`, прив'яжіть сенсор до мережі та отримайте `auth token` для прошивки.
                            3. Клієнт далі налаштує Wi-Fi і сповіщення через Bluetooth у своєму додатку.
                        </div>
                        <div className="admin-sensors-modal-actions admin-sensors-modal-span-2">
                            <button
                                type="button"
                                className="add-btn refresh"
                                onClick={() => handleVerifyLicense(newSensor)}
                                disabled={saving || verifyingLicense}
                            >
                                <ShieldCheck size={16} /> Перевірити sensor key
                            </button>
                            {licenseVerifyResult?.valid ? (
                                <span className="admin-sensors-verify-badge is-valid">
                                    Підтверджено{licenseVerifyResult.device_registered ? " • уже в мережі" : ""}
                                </span>
                            ) : null}
                        </div>
                        <input
                            name="display_name"
                            placeholder="Назва"
                            value={newSensor.display_name}
                            onChange={handleFieldChange(setNewSensor)}
                        />
                        <select
                            name="sensor_type"
                            value={newSensor.sensor_type}
                            onChange={handleFieldChange(setNewSensor)}
                            disabled={!sensorTypeOptions.length}
                        >
                            {!sensorTypeOptions.length && <option value="">Немає типів</option>}
                            {sensorTypeOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                        </select>
                        <select
                            name="sensor_family"
                            value={newSensor.sensor_family}
                            onChange={handleFieldChange(setNewSensor)}
                            disabled={!sensorFamilyOptions.length}
                        >
                            {!sensorFamilyOptions.length && <option value="">Немає сімейств</option>}
                            {sensorFamilyOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                        </select>
                        <input
                            name="hardware_model"
                            placeholder="Модель"
                            value={newSensor.hardware_model}
                            onChange={handleFieldChange(setNewSensor)}
                        />
                        <input
                            name="firmware_variant"
                            placeholder="Прошивка"
                            value={newSensor.firmware_variant}
                            onChange={handleFieldChange(setNewSensor)}
                        />
                        <input
                            name="pairing_code"
                            placeholder="Локальний код активації"
                            value={newSensor.pairing_code}
                            onChange={handleFieldChange(setNewSensor)}
                        />
                        <textarea
                            className="admin-sensors-modal-span-2"
                            name="descriptions"
                            placeholder="Опис"
                            value={newSensor.descriptions}
                            onChange={handleFieldChange(setNewSensor)}
                            rows={3}
                        />
                        <textarea
                            className="admin-sensors-modal-span-2"
                            name="install_notes"
                            placeholder="Нотатки монтажу"
                            value={newSensor.install_notes}
                            onChange={handleFieldChange(setNewSensor)}
                            rows={3}
                        />
                        <div className="admin-sensors-modal-span-2">
                            {renderFlagEditor(newSensor, setNewSensor, false)}
                        </div>
                    </div>
                    <div className="modal-actions">
                        <button className="add-btn" onClick={handleCreate} disabled={saving || !sensorTypeOptions.length}>Зареєструвати сенсор</button>
                        <button className="add-btn cancel-btn" onClick={() => setShowAddModal(false)} disabled={saving}>Скасувати</button>
                    </div>
                </div>
            </div>
        );
    };

    const renderCreatedModal = () => {
        if (!createdSensorInfo) return null;

        return (
            <div className="modal-backdrop" onClick={() => setCreatedSensorInfo(null)}>
                <div className="modal-content admin-sensors-modal" onClick={(event) => event.stopPropagation()}>
                    <h3>Сенсор активовано</h3>
                    <div className="admin-sensors-note">
                        Сенсор зареєстровано в мережі. Передайте клієнту код активації. `Auth token` потрібен лише для прошивки через кабель, якщо backend ще не прописаний на виробництві.
                    </div>
                    <div className="admin-sensors-created-grid">
                        <div><strong>Серійний номер:</strong> {createdSensorInfo.device_uid}</div>
                        <div><strong>Код активації:</strong> {createdSensorInfo.pairing_code}</div>
                        <div className="admin-sensors-modal-span-2"><strong>Auth token:</strong> {createdSensorInfo.auth_token}</div>
                    </div>
                    <div className="modal-actions">
                        <button className="add-btn" onClick={() => setCreatedSensorInfo(null)}>Закрити</button>
                    </div>
                </div>
            </div>
        );
    };

    const renderExtendModal = () => {
        if (!extendTarget) return null;

        return (
            <div className="modal-backdrop" onClick={closeExtendModal}>
                <div
                    className="modal-content admin-sensors-modal admin-sensors-extend-modal"
                    onClick={(event) => event.stopPropagation()}
                >
                    <h3>Продовжити ліцензію</h3>
                    <div className="admin-sensors-note">
                        {extendTarget.device_uid}
                        {extendTarget.display_name ? ` • ${extendTarget.display_name}` : ""}
                        {extendTarget.sensor_license_expires_at_utc
                            ? ` • зараз до ${formatDateTime(extendTarget.sensor_license_expires_at_utc)}`
                            : ""}
                    </div>
                    <label className="admin-sensors-extend-field">
                        <span>Нова дата завершення</span>
                        <input
                            type="date"
                            value={extendDate}
                            min={tomorrowLocalYmd()}
                            onChange={(event) => setExtendDate(event.target.value)}
                            disabled={saving}
                        />
                    </label>
                    <div className="modal-actions">
                        <button
                            className="add-btn"
                            onClick={handleConfirmExtendLicense}
                            disabled={saving || !extendDate}
                        >
                            Продовжити
                        </button>
                        <button
                            className="add-btn cancel-btn"
                            onClick={closeExtendModal}
                            disabled={saving}
                        >
                            Скасувати
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderLicenseActionButtons = (sensor, { iconClassName = "icon-btn", buttonClassName = "icon-button", iconSize } = {}) => {
        const renderIcon = (IconCmp) => (
            iconSize != null
                ? <IconCmp size={iconSize} />
                : <IconCmp className={iconClassName} />
        );

        return (
            <>
                <button
                    type="button"
                    className={buttonClassName}
                    onClick={() => handleExtendLicense(sensor)}
                    title="Продовжити ліцензію"
                    aria-label="Продовжити ліцензію"
                    disabled={saving}
                >
                    {renderIcon(CalendarClock)}
                </button>
                {sensor.sensor_license_blocked ? (
                    <button
                        type="button"
                        className={buttonClassName}
                        onClick={() => handleUnblockLicense(sensor)}
                        title="Розблокувати"
                        aria-label="Розблокувати"
                        disabled={saving}
                    >
                        {renderIcon(ShieldCheck)}
                    </button>
                ) : (
                    <button
                        type="button"
                        className={buttonClassName}
                        onClick={() => handleBlockLicense(sensor)}
                        title="Заблокувати"
                        aria-label="Заблокувати"
                        disabled={saving}
                    >
                        {renderIcon(Ban)}
                    </button>
                )}
            </>
        );
    };

    return (
        <div className="admin-sensors-shell">
            <div className="admin-sensors-note">
                «+» реєструє новий сенсор з LIC1 один раз (серійний номер + sensor key → пристрій у мережі). Платне продовження — кнопка «Продовжити» без перепрошивки. При втраті/крадіжці — «Заблокувати».
            </div>

            <div className="section admin-sensors-section">
                <div className="config-header">
                    <div className="header-buttons">
                        <button
                            className="add-btn toolbar-icon-btn"
                            onClick={() => { setShowAddModal(true); setLicenseVerifyResult(null); }}
                            disabled={saving}
                            title="Зареєструвати сенсор"
                            aria-label="Зареєструвати сенсор"
                        >
                            <Plus size={16} />
                        </button>
                        <button
                            className="add-btn refresh toolbar-icon-btn"
                            onClick={loadSensors}
                            disabled={loading || saving}
                            title="Оновити"
                            aria-label="Оновити"
                        >
                            <RotateCw size={16} />
                        </button>
                        <FilterPopover
                            active={visibilityFilter !== "all" || attachmentFilter !== "all" || licenseFilter !== "all"}
                            title="Фільтри сенсорів"
                            buttonTitle="Фільтри сенсорів"
                            onClear={() => {
                                setVisibilityFilter("all");
                                setAttachmentFilter("all");
                                setLicenseFilter("all");
                            }}
                        >
                            <label className="toolbar-filter-field">
                                <span>Видимість</span>
                                <select value={visibilityFilter} onChange={(event) => setVisibilityFilter(event.target.value)}>
                                    <option value="all">Усі</option>
                                    <option value="public">Публічні</option>
                                    <option value="private">Приватні</option>
                                </select>
                            </label>
                            <label className="toolbar-filter-field">
                                <span>Прикріплення</span>
                                <select value={attachmentFilter} onChange={(event) => setAttachmentFilter(event.target.value)}>
                                    <option value="all">Усі</option>
                                    <option value="attached">Прикріплені</option>
                                    <option value="free">Вільні</option>
                                </select>
                            </label>
                            <label className="toolbar-filter-field">
                                <span>Ліцензія</span>
                                <select value={licenseFilter} onChange={(event) => setLicenseFilter(event.target.value)}>
                                    <option value="all">Усі</option>
                                    <option value="active">Активні</option>
                                    <option value="inactive">Неактивні</option>
                                </select>
                            </label>
                        </FilterPopover>
                        <ToolbarSearch
                            value={searchQuery}
                            onChange={setSearchQuery}
                            placeholder="Пошук за серійним номером, назвою, типом, сімейством або моделлю..."
                        />
                    </div>
                </div>

                {message ? <div className="message-banner">{message}</div> : null}

                {renderModal()}
                {renderCreatedModal()}
                {renderExtendModal()}

                {isMobile ? (
                    sortedSensors.length ? (
                        <div className="config-grid">
                            {sortedSensors.map((sensor) => {
                                const isEditing = editingId === sensor.id;
                                const form = isEditing ? editedData : sensor;

                                return (
                                    <CollapsibleMobileCard
                                        key={sensor.id}
                                        expanded={expandedId === sensor.id}
                                        editing={isEditing}
                                        onToggle={() => setExpandedId((cur) => (cur === sensor.id ? null : sensor.id))}
                                        title={sensor.display_name || sensor.device_uid || "Сенсор"}
                                        meta={<span>{`Ліцензія: ${getLicenseBadgeLabel(sensor)}`}</span>}
                                        preview={`${sensor.device_uid || "-"} · ${sensor.sensor_type || "-"} · ${sensor.sensor_family || "-"}`}
                                        actions={(
                                            isEditing ? (
                                                <AdminCardActions
                                                    editing
                                                    onEdit={() => handleEdit(sensor)}
                                                    onSave={() => handleSave(sensor.id)}
                                                    onCancel={handleCancelEdit}
                                                    disabled={saving}
                                                />
                                            ) : (
                                                <div className="admin-card-actions is-idle">
                                                    <button
                                                        type="button"
                                                        className="admin-card-actions__button is-primary-edit"
                                                        onClick={() => handleEdit(sensor)}
                                                        title="Редагувати"
                                                        aria-label="Редагувати"
                                                        disabled={saving}
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                    {renderLicenseActionButtons(sensor, {
                                                        buttonClassName: "admin-card-actions__button",
                                                        iconSize: 16,
                                                    })}
                                                </div>
                                            )
                                        )}
                                    >
                                        <div>
                                            <span className="collapsible-card__field-label">Серійний номер</span>
                                            {isEditing ? (
                                                <input name="device_uid" value={form.device_uid || ""} readOnly disabled />
                                            ) : sensor.device_uid}
                                        </div>
                                        <div>
                                            <span className="collapsible-card__field-label">Назва</span>
                                            {isEditing ? (
                                                <input name="display_name" value={form.display_name || ""} onChange={handleFieldChange(setEditedData)} />
                                            ) : (sensor.display_name || "-")}
                                        </div>
                                        <div>
                                            <span className="collapsible-card__field-label">Тип</span>
                                            {isEditing ? (
                                                <select name="sensor_type" value={form.sensor_type || ""} onChange={handleFieldChange(setEditedData)} disabled={!sensorTypeOptions.length}>
                                                    {sensorTypeOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                                                </select>
                                            ) : (sensor.sensor_type || "-")}
                                        </div>
                                        <div>
                                            <span className="collapsible-card__field-label">Сімейство</span>
                                            {isEditing ? (
                                                <select name="sensor_family" value={form.sensor_family || ""} onChange={handleFieldChange(setEditedData)} disabled={!sensorFamilyOptions.length}>
                                                    {sensorFamilyOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                                                </select>
                                            ) : (sensor.sensor_family || "-")}
                                        </div>
                                        <div>
                                            <span className="collapsible-card__field-label">Модель</span>
                                            {isEditing ? (
                                                <input name="hardware_model" value={form.hardware_model || ""} onChange={handleFieldChange(setEditedData)} />
                                            ) : (sensor.hardware_model || "-")}
                                        </div>
                                        <div>
                                            <span className="collapsible-card__field-label">Прошивка</span>
                                            {isEditing ? (
                                                <input name="firmware_variant" value={form.firmware_variant || ""} onChange={handleFieldChange(setEditedData)} />
                                            ) : (sensor.firmware_variant || "-")}
                                        </div>
                                        <div>
                                            <span className="collapsible-card__field-label">Ліцензія</span>
                                            {getLicenseBadgeLabel(sensor)}
                                        </div>
                                        <div>
                                            <span className="collapsible-card__field-label">Діє до</span>
                                            {sensor.sensor_license_expires_at_utc ? formatDateTime(sensor.sensor_license_expires_at_utc) : "-"}
                                        </div>
                                        <div>
                                            <span className="collapsible-card__field-label">Активація прошивки</span>
                                            {formatDateTime(sensor.sensor_firmware_activated_at)}
                                        </div>
                                        <div>
                                            <span className="collapsible-card__field-label">Локальний код</span>
                                            {isEditing ? (
                                                <input name="pairing_code" value={form.pairing_code || ""} onChange={handleFieldChange(setEditedData)} />
                                            ) : (sensor.pairing_code || "-")}
                                        </div>
                                        <div>
                                            <span className="collapsible-card__field-label">Використовує</span>
                                            {getAttachmentLabel(sensor)}
                                        </div>
                                        <div>
                                            <span className="collapsible-card__field-label">Опис</span>
                                            {isEditing ? (
                                                <textarea name="descriptions" value={form.descriptions || ""} onChange={handleFieldChange(setEditedData)} rows={3} />
                                            ) : (sensor.descriptions || "-")}
                                        </div>
                                        <div>
                                            <span className="collapsible-card__field-label">Монтаж</span>
                                            {isEditing ? (
                                                <textarea name="install_notes" value={form.install_notes || ""} onChange={handleFieldChange(setEditedData)} rows={3} />
                                            ) : (sensor.install_notes || "-")}
                                        </div>
                                        <div>
                                            <span className="collapsible-card__field-label">Режим і статус</span>
                                            <div className="admin-sensors-card-flags">
                                                {isEditing ? renderFlagEditor(form, setEditedData, false) : renderFlagsPreview(sensor)}
                                            </div>
                                        </div>
                                    </CollapsibleMobileCard>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="admin-sensors-empty">Сенсори ще не додані.</div>
                    )
                ) : sortedSensors.length ? (
                    <div className="table-responsive">
                        <table className="admin-sensors-table">
                            <thead>
                                <tr>
                                    <th style={{ cursor: "pointer" }} onClick={() => toggleSort("device_uid")}>Серійний номер {sortBy === "device_uid" && (sortDir === "asc" ? "▲" : "▼")}</th>
                                    <th style={{ cursor: "pointer" }} onClick={() => toggleSort("display_name")}>Назва {sortBy === "display_name" && (sortDir === "asc" ? "▲" : "▼")}</th>
                                    <th style={{ cursor: "pointer" }} onClick={() => toggleSort("sensor_type")}>Тип {sortBy === "sensor_type" && (sortDir === "asc" ? "▲" : "▼")}</th>
                                    <th style={{ cursor: "pointer" }} onClick={() => toggleSort("sensor_family")}>Сімейство {sortBy === "sensor_family" && (sortDir === "asc" ? "▲" : "▼")}</th>
                                    <th>Модель</th>
                                    <th>Прошивка</th>
                                    <th>Ліцензія сенсора</th>
                                    <th>Діє до</th>
                                    <th>Активація прошивки</th>
                                    <th>Код активації</th>
                                    <th>Використовує</th>
                                    <th>Опис</th>
                                    <th>Монтаж</th>
                                    <th>Режим і статус</th>
                                    <th>Дії</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedSensors.map((sensor) => {
                                    const isEditing = editingId === sensor.id;
                                    const form = isEditing ? editedData : sensor;

                                    return (
                                        <tr key={sensor.id}>
                                            {SENSOR_TABLE_LEAD_FIELDS.map((field) => (
                                                <td key={field} className={field === "descriptions" || field === "install_notes" ? "admin-sensors-cell-text" : ""}>
                                                    {isEditing ? (
                                                        field === "sensor_type" ? (
                                                            <select name={field} value={form[field] || ""} onChange={handleFieldChange(setEditedData)} disabled={!sensorTypeOptions.length}>
                                                                {sensorTypeOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                                                            </select>
                                                        ) : field === "sensor_family" ? (
                                                            <select name={field} value={form[field] || ""} onChange={handleFieldChange(setEditedData)} disabled={!sensorFamilyOptions.length}>
                                                                {sensorFamilyOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                                                            </select>
                                                        ) : field === "descriptions" || field === "install_notes" ? (
                                                            <textarea name={field} value={form[field] || ""} onChange={handleFieldChange(setEditedData)} rows={3} />
                                                        ) : (
                                                            <input name={field} value={form[field] || ""} onChange={handleFieldChange(setEditedData)} />
                                                        )
                                                    ) : (
                                                        <span>{sensor[field] || "-"}</span>
                                                    )}
                                                </td>
                                            ))}
                                            <td>
                                                <span className={`admin-sensors-status-badge ${getLicenseBadgeClass(sensor)}`}>
                                                    {getLicenseBadgeLabel(sensor)}
                                                </span>
                                            </td>
                                            <td>
                                                <span>{sensor.sensor_license_expires_at_utc ? formatDateTime(sensor.sensor_license_expires_at_utc) : "-"}</span>
                                            </td>
                                            <td>
                                                <span>{formatDateTime(sensor.sensor_firmware_activated_at)}</span>
                                            </td>
                                            <td>
                                                {isEditing ? (
                                                    <input name="pairing_code" value={form.pairing_code || ""} onChange={handleFieldChange(setEditedData)} />
                                                ) : (
                                                    <span>{sensor.pairing_code || "-"}</span>
                                                )}
                                            </td>
                                            <td>
                                                <span>{getAttachmentLabel(sensor)}</span>
                                            </td>
                                            {SENSOR_TABLE_TRAIL_FIELDS.filter((field) => field !== "pairing_code").map((field) => (
                                                <td key={field} className={field === "descriptions" || field === "install_notes" ? "admin-sensors-cell-text" : ""}>
                                                    {isEditing ? (
                                                        field === "descriptions" || field === "install_notes" ? (
                                                            <textarea name={field} value={form[field] || ""} onChange={handleFieldChange(setEditedData)} rows={3} />
                                                        ) : (
                                                            <input name={field} value={form[field] || ""} onChange={handleFieldChange(setEditedData)} />
                                                        )
                                                    ) : (
                                                        <span>{sensor[field] || "-"}</span>
                                                    )}
                                                </td>
                                            ))}
                                            <td className="admin-sensors-cell-flags">
                                                {isEditing ? renderFlagEditor(form, setEditedData, false) : renderFlagsPreview(sensor)}
                                            </td>
                                            <td className="actions">
                                                {isEditing ? (
                                                    <>
                                                        <button className="icon-button" onClick={() => handleSave(sensor.id)} title="Зберегти" disabled={saving}>
                                                            <Save className="icon-btn" />
                                                        </button>
                                                        <button className="icon-button" onClick={handleCancelEdit} title="Скасувати" disabled={saving}>
                                                            <X className="icon-btn" />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button className="icon-button" onClick={() => handleEdit(sensor)} title="Редагувати" disabled={saving}>
                                                            <Edit className="icon-btn" />
                                                        </button>
                                                        {renderLicenseActionButtons(sensor)}
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="admin-sensors-empty">Сенсори ще не додані.</div>
                )}
            </div>

        </div>
    );
};

export default SensorsPageAdmins;
